import { ADVISORS_BASE } from '@src/components/sco/Advisors/constants'
import { DIR_CATEGORIES } from '@src/components/sco/Advisors/data/dirCategories'
import type { SEOMetadataProps } from '@src/components/sco/utilities/SEOMetadata'
import { startupsUrl } from '@src/lib/startups-url'

/**
 * Title / description / canonical / OG for the public Advisors pages (Phase 3 SEO).
 * Canonicals drop filters and paging: `?q=` and `?acceptingSessions=` canonical to the bare page;
 * a category (path or `?category=`) canonicals to `/advisors/directory/{key}` — the URL in the sitemap.
 */

const ogImage = (title: string) => startupsUrl('/api/og-image', { title })

const page = (title: string, description: string, path: string): SEOMetadataProps => ({
  title,
  description,
  url: startupsUrl(path),
  imageUrl: ogImage(title),
  type: 'website',
})

export const ADVISORS_LANDING_TITLE = 'Startup Advisors & Wil.ai'
export const ADVISORS_LANDING_DESCRIPTION =
  'The advice you need, the moment you need it. A 24/7 AI advisor blended with real-world experts for funding, pitch decks, customers, and product.'

export const advisorsLandingSeo = (): SEOMetadataProps =>
  page(ADVISORS_LANDING_TITLE, ADVISORS_LANDING_DESCRIPTION, ADVISORS_BASE)

export const advisorsBrowseSeo = (): SEOMetadataProps =>
  page(
    'Browse Startup Advisors by Topic',
    'Find a startup advisor by specialty: funding, customer acquisition, product, engineering, hiring, finance, legal, brand and leadership.',
    `${ADVISORS_BASE}/browse`
  )

export const advisorsDirectorySeo = (categoryKey?: string | null): SEOMetadataProps => {
  if (categoryKey) {
    const category = DIR_CATEGORIES.find(cat => cat.key === categoryKey)
    if (!category) {
      // Not one of the sitemap's category pages: keep it out of the index.
      return { ...advisorsDirectorySeo(null), noIndex: true }
    }
    return page(
      `${category.name} Advisors for Startups`,
      `${category.value} Browse approved ${category.name.toLowerCase()} advisors for your board or a 1:1 session.`,
      `${ADVISORS_BASE}/directory/${encodeURIComponent(category.key)}`
    )
  }
  return page(
    'Find Startup Advisors',
    'Browse approved startup advisors for board seats and 1:1 sessions — funding, product, customer acquisition, hiring and more.',
    `${ADVISORS_BASE}/directory`
  )
}

/** `/advisors/sessions/new` — the booking form shows prices to guests (L2), so never indexed. */
export const advisorsBookSeo = (): SEOMetadataProps => ({
  title: 'Book a session',
  noIndex: true,
  noFollow: true,
})
